import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useReducer,
  useState,
} from "react";
import navigatorReducer, {
  initialState,
  setCompetency,
  setLearnerOutcome,
  setSubCompetency,
  setPerformanceLevel,
} from "@/contexts/navigator/reducer";
import { Competency, LearnerOutcome } from "@/lib/models";
import {
  handleDrawerClose,
  handleDrawerOpen,
  handleMapListViewToggleButtonClick,
} from "@/lib/analytics/navigation";
import { useEntities } from "../entities/EntitiesContext";

export type NavigatorContextState = {
  readonly activeLearnerOutcomeId: string | null;
  readonly activeCompetencyId: string | null;
  readonly activeSubCompetencyId: string | null;
  readonly activePerformanceLevelId: string | null;
  readonly updateLearnerOutcome: (id: string) => void;
  readonly updateCompetency: (id: string) => void;
  readonly updateSubCompetency: (id: string) => void;
  readonly updatePerformanceLevel: (id: string) => void;
  readonly reset: () => void;
  readonly selectedEntity: Competency | LearnerOutcome | null;
  readonly handleDrawerSelection: (entity: Competency | LearnerOutcome) => void;
  readonly resetDrawerSelection: () => void;
  readonly isOverviewMapActive: boolean;
  readonly toggleOverviewMap: () => void;
  readonly areComponentSkillsActive: boolean;
  readonly updateAreComponentSkillsActive: (isCS: boolean) => void;
};

export const NavigatorContext = createContext<NavigatorContextState>({
  activeLearnerOutcomeId: null,
  activeCompetencyId: null,
  activeSubCompetencyId: null,
  activePerformanceLevelId: null,
  updateLearnerOutcome: () => undefined,
  updateCompetency: () => undefined,
  updateSubCompetency: () => undefined,
  updatePerformanceLevel: () => undefined,
  reset: () => undefined,
  selectedEntity: null,
  handleDrawerSelection: () => undefined,
  resetDrawerSelection: () => undefined,
  isOverviewMapActive: true,
  toggleOverviewMap: () => undefined,
  areComponentSkillsActive: true,
  updateAreComponentSkillsActive: () => undefined,
});

type NavigatorContextProviderProps = {
  children: ReactNode;
};

const NavigatorProvider = ({ children }: NavigatorContextProviderProps) => {
  const [state, dispatch] = useReducer(navigatorReducer, initialState);
  const [drawerEntity, setDrawerEntity] = useState<
    Competency | LearnerOutcome | null
  >(null);
  const [isOverviewMapActive, setIsOverviewMapActive] = useState(true);

  const [areComponentSkillsActive, setAreComponentSkillsActive] =
    useState(true);

  const { getEntityHierarchyFromId } = useEntities();

  const handleDrawerSelection = useCallback(
    (entity: Competency | LearnerOutcome) => {
      setDrawerEntity((prev) => {
        if (prev && prev.id === entity.id) {
          handleDrawerClose(getEntityHierarchyFromId(entity.id));
          return null;
        } else {
          handleDrawerOpen(getEntityHierarchyFromId(entity.id));
          return entity;
        }
      });
    },
    [getEntityHierarchyFromId],
  );

  const resetDrawerSelection = useCallback(() => {
    setDrawerEntity((prev) => {
      handleDrawerClose(getEntityHierarchyFromId(prev?.id || ""));
      return null;
    });
  }, [getEntityHierarchyFromId]);

  const toggleOverviewMap = useCallback(() => {
    setIsOverviewMapActive((isOverviewMapActive) => {
      const updatedView = isOverviewMapActive ? "List" : "Map";
      const trackEventProps = {
        buttonName: "Toggle View",
        action: `Toggle ${updatedView} View`,
      };
      handleMapListViewToggleButtonClick(trackEventProps);

      return !isOverviewMapActive;
    });
  }, []);

  const updateAreComponentSkillsActive = useCallback((isCS: boolean) => {
    setAreComponentSkillsActive(isCS);
  }, []);

  const updateLearnerOutcome = useCallback(
    (learnerOutcomeId: string | null) =>
      dispatch(setLearnerOutcome(learnerOutcomeId)),
    [dispatch],
  );
  const updateCompetency = useCallback(
    (competencyId: string | null) => dispatch(setCompetency(competencyId)),
    [dispatch],
  );
  const updateSubCompetency = useCallback(
    (subCompetencyId: string | null) =>
      dispatch(setSubCompetency(subCompetencyId)),
    [dispatch],
  );
  const updatePerformanceLevel = useCallback(
    (performanceLevelId: string | null) =>
      dispatch(setPerformanceLevel(performanceLevelId)),
    [dispatch],
  );
  const reset = useCallback(() => {
    dispatch(setLearnerOutcome(null));
    dispatch(setCompetency(null));
    dispatch(setSubCompetency(null));
    dispatch(setPerformanceLevel(null));
  }, [dispatch]);

  return (
    <NavigatorContext.Provider
      value={{
        activeLearnerOutcomeId: state.learnerOutcome,
        activeCompetencyId: state.competency,
        activeSubCompetencyId: state.subCompetency,
        activePerformanceLevelId: state.performanceLevel,
        updateLearnerOutcome,
        updateCompetency,
        updateSubCompetency,
        updatePerformanceLevel,
        reset,
        selectedEntity: drawerEntity,
        handleDrawerSelection,
        resetDrawerSelection,
        isOverviewMapActive,
        toggleOverviewMap,
        areComponentSkillsActive,
        updateAreComponentSkillsActive,
      }}
    >
      {children}
    </NavigatorContext.Provider>
  );
};

export default NavigatorProvider;

export const useNavigator = () => {
  return useContext(NavigatorContext);
};
